import { throttle } from "lodash"
import PropTypes from "prop-types"
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Suspense,
} from "react"
import styled from "styled-components"

import { isTouchDevice } from "helpers/browser"

import { StyledImage } from "./components"

/**
 * High Order Component for making Image zoomable/ magnifiable
 * @param {React Component} WrappedComponent
 */

const ZoomBox = styled.div`
  height: auto;
  margin: 0 auto;
  max-width: 500px;
  position: relative;
  text-align: center;
  width: 100%;

  &:hover,
  &:active {
    display: block;
  }
`

const MagnifyingGlass = styled.div`
  background: url(${(props) => props.magnifiedImage}) no-repeat #fff;
  border: 1px solid ${(props) => props.theme.colors.grey20};
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  display: block;
  height: ${(props) => props.mgHeight}px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.2s;
  width: ${(props) => props.mgWidth}px;
  z-index: 99;

  &.active {
    opacity: 1;
  }
`

/* As synthetic event is being reused, the event will not persist its values.
  When later code access any properties of the event, it might return null.
  Use closure to "memorize" the `clientX` and `clientY` for later use.
  https‍://fb‍.me/react-event-pooling
  https://stackoverflow.com/a/37312154
 */
function throttleEventHandler(...args) {
  const throttled = throttle(...args)
  return function (e) {
    const { clientX, clientY } = e
    return throttled({ clientX, clientY })
  }
}

const mgHeight = 190
const mgWidth = 210
const mgZoomFactor = 4

const MagnifyImage = ({ mgShowZoom, ...props }) => {
  const zoomBoxRef = useRef()
  const magnifiedRef = useRef()
  // Magnifying Image mouse cursor w.r.t. Zoombox
  // (x: 0, y: 0) => Mouse cursor at the top left corner of Zoombox
  const [mgCursorX, setMgCursorX] = useState(0)
  const [mgCursorY, setMgCursorY] = useState(0)

  useEffect(() => {
    if (mgShowZoom && zoomBoxRef.current && magnifiedRef.current) {
      initMgCursor()
    }
  }, [mgShowZoom, zoomBoxRef.current, magnifiedRef.current])

  useEffect(() => {
    updateMagnifyingGlass()
  }, [mgCursorX, mgCursorY])

  const initMgCursor = () => {
    const zoomBoxRect = zoomBoxRef.current.getBoundingClientRect()
    setMgCursorX(Math.floor(zoomBoxRect.width / 2))
    setMgCursorY(Math.floor(zoomBoxRect.height / 2))
  }

  const updateMagnifyingGlass = () => {
    if (!zoomBoxRef.current) return
    const zoomBoxRect = zoomBoxRef.current.getBoundingClientRect()

    let relX = (mgCursorX / zoomBoxRect.width) * 100
    let relY = (mgCursorY / zoomBoxRect.height) * 100

    // //lets user scroll past right edge of image
    if (mgCursorX > 0.01 * zoomBoxRect.width) {
      relX += 0.15 * relX
    }

    // //lets user scroll past bottom edge of image
    if (mgCursorY >= 0.01 * zoomBoxRect.height) {
      relY += 0.15 * relY
    }

    // Define Position of the magnify image
    const magnified = magnifiedRef.current
    const style = magnified?.style
    if (!style) return
    style.backgroundPositionX = `${relX - 9}%`
    style.backgroundPositionY = `${relY - 9}%`
    // Define magnifying glass position
    const mgBoxLeft = mgCursorX - Math.floor(mgWidth / 2) - 10
    const mgBoxTop = mgCursorY - Math.floor(mgHeight / 2) - 10
    // Set Position of the magnifying glass
    // Prevent magnifying glass move out of the Zoombox boundary
    style.left = `${Math.min(
      Math.max(mgBoxLeft, 0),
      zoomBoxRect.width - mgWidth
    )}px`
    style.top = `${Math.min(
      Math.max(mgBoxTop, 0),
      zoomBoxRect.height - mgHeight
    )}px`

    style.backgroundSize = `${mgZoomFactor * 100}% ${mgZoomFactor * 100}%`
  }

  const onMouseMove = (e) => {
    if (!mgShowZoom) return

    const zoomBoxRect = zoomBoxRef.current.getBoundingClientRect()
    const newMgCursorX = Math.max(e.clientX - zoomBoxRect.x, 0)
    const newMgCursorY = Math.max(e.clientY - zoomBoxRect.y, 0)
    setMgCursorX(newMgCursorX)
    setMgCursorY(newMgCursorY)
  }

  const throttledOnMouseMove = useCallback(
    throttleEventHandler(onMouseMove, 20),
    [zoomBoxRef.current, setMgCursorX, setMgCursorY, mgShowZoom]
  )

  const srcList = props.srcSet?.split(",")
  const highResSrc =
    srcList?.length > 0
      ? srcList[srcList.length - 1].trim().split(" ")[0]
      : props.src

  return (
    <ZoomBox onMouseMove={throttledOnMouseMove} ref={zoomBoxRef}>
      <StyledImage draggable="false" {...props} />
      {typeof window !== "undefined" && !isTouchDevice() && (
        <Suspense fallback={<div />}>
          <MagnifyingGlass
            className={
              mgShowZoom
                ? "active fs-ignore-rage-clicks"
                : "fs-ignore-rage-clicks"
            }
            magnifiedImage={highResSrc}
            mgHeight={mgHeight}
            mgWidth={mgWidth}
            ref={magnifiedRef}
          />
        </Suspense>
      )}
    </ZoomBox>
  )
}
MagnifyImage.propTypes = {
  mgShowZoom: PropTypes.bool,
  src: PropTypes.string,
  srcSet: PropTypes.string,
}

export default MagnifyImage
